import React, { useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import icd10Service from '../../services/icd10Service';

/**
 * Icd10Picker — type a code ("K80.2") or words ("gallstones"), pick an ICD-10 code.
 * Uses the same hybrid search as the coding workflow; only billable codes are offered.
 * onPick(row) gets the code row; the box clears afterwards.
 */
const Icd10Picker = ({ onPick, exclude = [], disabled = false, autoFocus = false, placeholder = 'Search ICD-10 — a code or words…', onCancel }) => {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const text = q.trim();
    if (text.length < 2) { setResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const out = await icd10Service.search(text, { limit: 12 });
        if (!cancelled) { setResults(out.results.filter((r) => !exclude.includes(r.code))); setActive(0); setOpen(true); }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setBusy(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // exclude is compared by value on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, exclude.join(',')]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const pick = (row) => {
    onPick(row);
    setQ('');
    setResults([]);
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); if (open) setOpen(false); else onCancel?.(); return; }
    if (!open || !results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter') { e.preventDefault(); pick(results[active]); }
  };

  return (
    <div ref={boxRef} className="relative">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onKeyDown={onKey}
        disabled={disabled}
        autoFocus={autoFocus}
        placeholder={placeholder}
        className="w-full rounded-lg border border-slate-300 py-1.5 pl-8 pr-3 text-sm focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30 disabled:bg-slate-50"
      />
      {open && q.trim().length >= 2 && (
        <ul className="absolute z-30 mt-1 max-h-72 w-full min-w-[20rem] overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {results.map((r, i) => (
            <li key={r.code}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(r)}
                onMouseEnter={() => setActive(i)}
                className={`flex w-full items-start gap-3 px-3 py-1.5 text-left text-sm ${i === active ? 'bg-teal-50' : ''}`}
              >
                <span className="w-14 shrink-0 font-mono font-semibold text-brand-navy">{r.code}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-slate-800">{r.description}</span>
                  <span className="block text-[11px] text-slate-400">
                    {[r.valid_primary ? 'Primary allowed' : 'Secondary only', r.gender && (r.gender === 'F' ? 'Female only' : 'Male only'), r.age_range && `Age ${r.age_range}`].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </button>
            </li>
          ))}
          {results.length === 0 && <li className="px-3 py-2 text-xs text-slate-400">{busy ? 'Searching…' : 'No billable codes match.'}</li>}
        </ul>
      )}
    </div>
  );
};

export default Icd10Picker;
